import { rememberActiveProperty } from '@/lib/property-store';
import { useQueryClient } from '@tanstack/react-query';
import {
  CHANNEL_META,
  DEFAULT_INTERVAL,
  DEFAULT_PLAN,
  PLATFORM_LABEL,
  planPrice,
  TRIAL_DAYS,
  LEGAL,
  TRIAL_PACK,
  type BillingInterval,
  type Channel,
  type PlanKey,
  PLAN_META,
} from '@str/shared';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { CardFields, type ConfirmCard } from '@/components/CardFields';
import { GoogleButton } from '@/components/GoogleButton';
import { PlanPicker } from '@/components/PlanPicker';
import { StripeBadge } from '@/components/StripeBadge';
import { Check, ErrorText, Logo, Spinner, StepIndicator } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { cardBrandLabel } from '@/lib/card-on-file';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { addDays, shortDate, toIsoDate } from '@/lib/format';
import { billingApi, keys, onboardingApi, useMe, useProperty } from '@/lib/queries';
import { mediaStyle } from '@/lib/scenes';
import { trialTerms } from '@/lib/trial-terms';
import { track } from '@/lib/analytics';

function channelNames(channels: readonly string[]) {
  const names = channels.map((c) => CHANNEL_META[c as Channel]?.label ?? c);
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
    : (names[0] ?? '');
}

export function Signup() {
  const [params] = useSearchParams();
  const propertyId = params.get('property');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const property = useProperty(propertyId);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [plan, setPlan] = useState<PlanKey>(DEFAULT_PLAN);
  const [pickedInterval, setInterval] = useState<BillingInterval>(DEFAULT_INTERVAL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<ConfirmCard | null>(null);
  const onCardReady = useCallback((fn: ConfirmCard) => {
    confirmRef.current = fn;
  }, []);

  const user = me.data?.user ?? null;
  // The account form is on screen: /api/me answered and nobody is signed in.
  const formSeen = useRef(false);
  const showsForm = !!me.data && !user;
  useEffect(() => {
    if (!showsForm || formSeen.current) return;
    formSeen.current = true;
    track('signup_form_viewed', { has_property: !!propertyId });
  }, [showsForm, propertyId]);
  // No default: until /api/me answers we do not know whether this is live Stripe or the mock, and
  // guessing mock flashes the test-card line to real visitors.
  const billing = me.data?.billing ?? null;
  const sub = me.data?.subscription ?? null;
  // A host on a plan adding a property: no card, no trial, the tier is added today, prorated.
  const attaching = !!user && !!sub?.active && !!propertyId && !property.data?.plan;
  // One trial per account: a host whose plan has ended is charged today on the way back.
  const returning = !!sub && !sub.active && !attaching;
  // A plan still running (renewing, or cancelled but paid up to its period end) has nothing to
  // start or restart here (R24).
  const running = !!sub?.active && !attaching;
  const trialEnd = shortDate(toIsoDate(addDays(new Date(), returning ? 0 : TRIAL_DAYS)));
  const interval: BillingInterval = attaching && sub ? sub.interval : pickedInterval;
  const onTrial = sub?.status === 'trialing';
  const firstCharge = sub?.trialEndsAt ? shortDate(sub.trialEndsAt) : trialEnd;
  const addLine = sub
    ? onTrial
      ? `$${planPrice(plan, interval).totalCents / 100} ${interval === 'year' ? 'a year' : 'a month'} from your first charge on ${firstCharge}; nothing today`
      : interval === 'year'
        ? `$${planPrice(plan, 'year').totalCents / 100} a year, prorated to your renewal${sub.currentPeriodEnd ? ` on ${shortDate(sub.currentPeriodEnd)}` : ''}`
        : `$${planPrice(plan, 'month').totalCents / 100} a month from today, prorated`
    : '';
  const name = property.data?.name ?? 'your listing';
  const hero = property.data?.media.find((m) => m.source === 'listing');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (!user) {
        if (!fullName.trim()) throw new Error('Enter your name so we can sign your posts');
        if (!email || !password) throw new Error('Enter your email and a password (8+ characters)');
        track('signup_submitted', { plan, interval, has_property: !!propertyId });
        const res = await authClient.signUp.email({
          name: fullName.trim(),
          email,
          password,
          callbackURL: '/app',
        });
        if (res.error) throw new Error(authErrorMessage(res.error));
      }
      if (propertyId) await onboardingApi.claim(propertyId);
      await qc.invalidateQueries({ queryKey: keys.me });
      if (attaching && propertyId) {
        await billingApi.attachPlan(propertyId, plan);
        rememberActiveProperty(propertyId);
        await qc.invalidateQueries({ queryKey: keys.me });
        void navigate('/app', { replace: true, state: { toast: `Added ${name} to your plan` } });
        return;
      }
      if (!confirmRef.current) throw new Error('Card form is not ready yet');
      const paymentMethodId = await confirmRef.current();
      await billingApi.subscribe({ plan, paymentMethodId, interval });
      await qc.invalidateQueries({ queryKey: keys.me });
      if (propertyId) rememberActiveProperty(propertyId);
      void navigate('/app', {
        replace: true,
        state: {
          toast: returning
            ? 'Plan restarted · welcome back'
            : `Account created · trial started, $${TRIAL_PACK.priceCents / 100} today`,
        },
      });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
      await qc.invalidateQueries({ queryKey: keys.me });
    }
  };

  // The listing in the link is not here to claim: it is already in an account, it belongs to an
  // import in another browser, or it is gone. The API does not say which (it never shows whether
  // someone else's property exists), so neither does this; it offers the two ways on (J22).
  if (propertyId && property.isError) {
    return (
      <div className="screen signup-page">
        <div className="topbar">
          <Logo />
        </div>
        <div
          className="auth-card"
          style={{ width: 'calc(100% - 32px)', maxWidth: 460, margin: '40px auto', gap: 14 }}
        >
          <h1 style={{ fontSize: 28, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-.03em' }}>
            This listing is not waiting for an account
          </h1>
          <p style={{ fontSize: 15, color: '#666', lineHeight: 1.5 }}>
            {user
              ? `It may already be in another account, or the import has expired. You're signed in as ${user.email}.`
              : 'If you already made an account with it, log in and it is there. If not, the import has expired or was made in another browser: import your listing again, it takes a minute or two.'}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {user ? (
              <Link to="/app" className="btn btn-primary">
                Open your posts
              </Link>
            ) : (
              <Link to="/login" className="btn btn-primary">
                Log in
              </Link>
            )}
            <Link to="/" className="btn btn-ghost">
              Import your listing again
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (running && sub) {
    const until = sub.currentPeriodEnd ?? sub.trialEndsAt;
    return (
      <div className="screen signup-page">
        <div className="topbar">
          <Logo />
        </div>
        <div
          className="auth-card"
          style={{ width: 'calc(100% - 32px)', maxWidth: 460, margin: '40px auto', gap: 14 }}
        >
          <h1 style={{ fontSize: 28, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-.03em' }}>
            Your plan is still running
          </h1>
          <p style={{ fontSize: 15, color: '#666', lineHeight: 1.5 }}>
            {sub.cancelAtPeriodEnd
              ? `Your ${sub.planName} plan runs until ${until ? shortDate(until) : 'the end of this period'}. You can keep posting until then, or resume it from Settings to keep going after.`
              : `Your ${sub.planName} plan renews on ${until ? shortDate(until) : 'its next billing date'}. Nothing to start here.`}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/app" className="btn btn-primary">
              Open your posts
            </Link>
            <Link to="/app/settings" className="btn btn-ghost">
              Open Settings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen signup-page">
      <div className="topbar">
        <Logo />
        <StepIndicator current={3} />
      </div>
      <div className="signup-grid">
        <div className="a-rise-5" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h1
              style={{
                fontSize: 40,
                lineHeight: 1.02,
                fontWeight: 800,
                letterSpacing: '-.04em',
                textWrap: 'balance',
              }}
            >
              {attaching
                ? `Add ${name} to your plan.`
                : returning
                  ? 'Welcome back. Pick a plan to keep posting.'
                  : user
                    ? 'Pick a plan to keep going.'
                    : 'Your first post is ready to draft.'}
            </h1>
            <p style={{ fontSize: 15.5, color: '#666', lineHeight: 1.5 }}>
              {attaching
                ? `This adds ${PLAN_META[plan].name} for ${name}: ${addLine}. Its monthly credits join your wallet ${onTrial ? 'when your plan starts' : 'today'}.`
                : returning
                  ? `You're signed in as ${user?.email}. Your plan ended, so this one starts today and bills right away. Photos, brand DNA and drafts are all still here.`
                  : user
                    ? `You're signed in as ${user.email}. Start your ${TRIAL_DAYS}-day trial to schedule posts for ${name}: your first post and Reel are included for $${TRIAL_PACK.priceCents / 100}.`
                    : `Create your account to keep ${name} and everything we just imported. A ${TRIAL_DAYS}-day trial with your first post and Reel included for $${TRIAL_PACK.priceCents / 100}.`}
            </p>
          </div>
          <div
            className="hide-md"
            style={{
              position: 'relative',
              width: 300,
              aspectRatio: '4/5',
              borderRadius: 22,
              overflow: 'hidden',
              ...mediaStyle(hero ?? { scene: property.data?.heroScene ?? 'dusk' }),
              boxShadow: '0 40px 80px -30px rgba(0,0,0,.4),0 0 0 8px #fff',
              transform: 'rotate(-3deg)',
              margin: '14px 0 10px 10px',
              animation: 'pop .7s .15s cubic-bezier(.2,.8,.2,1) both',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: 22,
                background: 'linear-gradient(transparent 40%,rgba(31,42,36,.6))',
              }}
            >
              <span className="tag" style={{ alignSelf: 'flex-start', marginBottom: 'auto' }}>
                Draft · Image
              </span>
              <div
                className="serif"
                style={{
                  fontSize: 28,
                  lineHeight: 1.05,
                  letterSpacing: '-.02em',
                  color: '#fff',
                  textShadow: '0 2px 20px rgba(0,0,0,.35)',
                }}
              >
                {property.data?.heroHeadline ?? 'Your first post, in your words.'}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  color: 'rgba(255,255,255,.85)',
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                }}
              >
                {name} · {property.data?.location.split(',')[0] ?? ''}
              </div>
            </div>
            <div className="shimmer slow" />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              fontSize: 13.5,
              color: '#555',
            }}
          >
            {[
              property.data?.reviews?.length
                ? `${property.data.photoCount} photos, your description and ${property.data.reviews.length} reviews — saved`
                : `${property.data?.photoCount ?? 0} photos and your description — saved · ${property.data?.reviewCount ?? 0} reviews on ${property.data?.platform === 'direct' || !property.data ? 'your site' : PLATFORM_LABEL[property.data.platform]}`,
              property.data?.channels.length
                ? `${channelNames(property.data.channels)} found on your listing`
                : 'Connect Instagram & Facebook after your first post',
              attaching
                ? onTrial
                  ? `Nothing charged today · ${PLAN_META[plan].name} for ${name} joins your first charge on ${firstCharge}`
                  : `${PLAN_META[plan].name} for ${name} added today, prorated · stops at your next renewal if you remove it`
                : returning
                  ? 'Cancel anytime from Settings · no calls, no emails'
                  : `$${TRIAL_PACK.priceCents / 100} today, credited to your first ${interval} · nothing more until ${trialEnd} · cancel in one tap before then`,
            ].map((t) => (
              <span key={t} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Check size={18} />
                {t}
              </span>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="auth-card" style={{ animation: 'rise .5s .1s both' }}>
          {!user && (
            <>
              <GoogleButton
                enabled={!!me.data?.features.googleAuth}
                callbackURL={propertyId ? `/signup?property=${propertyId}` : '/signup'}
              />
              <div className="divider">or</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  className="input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  type="text"
                  autoComplete="name"
                  aria-label="Name"
                />
                <input
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  type="email"
                  autoComplete="email"
                  aria-label="Email"
                />
                <input
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  type="password"
                  autoComplete="new-password"
                  aria-label="Password"
                  minLength={8}
                />
              </div>
            </>
          )}
          {user && (
            <div
              style={{
                fontSize: 13,
                color: '#555',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {user.image ? (
                  <img
                    src={user.image}
                    alt=""
                    width={24}
                    height={24}
                    style={{ borderRadius: 99 }}
                    referrerPolicy="no-referrer"
                  />
                ) : null}
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    lineHeight: 1.3,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                  }}
                >
                  <b>{user.name}</b>
                  <span>{user.email}</span>
                </span>
              </span>
              <button
                type="button"
                className="hv"
                style={{ border: 0, background: 'none', color: '#999', padding: 0 }}
                onClick={() =>
                  void authClient.signOut().then(() => qc.invalidateQueries({ queryKey: keys.me }))
                }
              >
                Not you?
              </button>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="label-caps">
              {attaching
                ? `Tier for ${name}`
                : returning
                  ? 'Plan · billed today'
                  : `Plan · ${TRIAL_DAYS}-day trial`}
            </span>
            <PlanPicker
              value={plan}
              onChange={setPlan}
              interval={interval}
              onIntervalChange={setInterval}
              billedWhen={
                attaching
                  ? onTrial
                    ? `at your first charge on ${firstCharge}`
                    : 'today, prorated'
                  : returning
                    ? 'today'
                    : 'at day 7'
              }
              intervalLocked={attaching}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="label-caps">
              {attaching
                ? 'Card'
                : returning
                  ? 'Card · charged today'
                  : `Card · $${TRIAL_PACK.priceCents / 100} today · then not charged until ${trialEnd}`}
            </span>
            {attaching && sub ? (
              <span style={{ fontSize: 13 }}>
                {cardBrandLabel(sub.cardBrand)} ···· {sub.paymentLast4 ?? '····'} on file · change
                it from Settings
              </span>
            ) : billing ? (
              <CardFields
                mode={billing.mode}
                publishableKey={billing.publishableKey}
                onReady={onCardReady}
              />
            ) : me.isError ? (
              <ErrorText>We couldn’t load the card form. Refresh the page to try again.</ErrorText>
            ) : (
              <div
                className="cardgroup"
                role="status"
                aria-label="Loading the card form"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 44,
                }}
              >
                <Spinner />
              </div>
            )}
            {billing?.mode === 'mock' && !attaching && (
              <span style={{ fontSize: 11.5, color: '#999' }}>
                Test mode · use 4242 4242 4242 4242, any future date, any CVC
              </span>
            )}
            <StripeBadge />
          </div>
          <ErrorText>{error}</ErrorText>
          <p style={{ margin: 0, fontSize: 11.5, color: '#999' }}>
            {attaching || returning ? 'By continuing' : 'By starting your trial'} you agree to the{' '}
            <a href={LEGAL.terms} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
              Terms
            </a>{' '}
            and{' '}
            <a href={LEGAL.privacy} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
              Privacy Policy
            </a>
            .
          </p>
          <button type="submit" className="btn btn-primary btn-xl hv" disabled={busy}>
            {busy
              ? attaching
                ? 'Adding it…'
                : returning
                  ? 'Restarting your plan…'
                  : 'Setting up your trial…'
              : attaching
                ? `Add property · ${PLAN_META[plan].name}`
                : returning
                  ? 'Restart my plan'
                  : `Start my trial for $${TRIAL_PACK.priceCents / 100}`}{' '}
            <span style={{ fontSize: 17 }}>→</span>
          </button>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11.5,
              color: '#999',
            }}
          >
            <span>
              {attaching
                ? onTrial
                  ? `Nothing charged today · joins your first charge on ${firstCharge} · remove it any time from Settings`
                  : `Charged today, prorated · remove it any time from Settings; it stops at your next renewal`
                : returning
                  ? `$${planPrice(plan, interval).totalCents / 100}${interval === 'year' ? ' a year' : ' a month'} · charged today · cancel anytime`
                  : trialTerms(plan, interval, trialEnd)}
            </span>
            {propertyId ? (
              <Link to={`/review/${propertyId}`} style={{ color: '#999', textDecoration: 'none' }}>
                ← Back
              </Link>
            ) : (
              <Link to="/login" style={{ color: '#999', textDecoration: 'none' }}>
                Log in
              </Link>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
